/**
 * Granted Access List Component
 * 
 * Shows list of users who have been granted access (free access only)
 * Allows owner to revoke access
 */

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { revokeAccessGrant } from '../../utils/accessGrantHelpers';
import './GrantedAccessList.css';

const GrantedAccessList = ({ user }) => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;

    const fetchGrantedAccess = async () => {
      try {
        // Get all jobs owned by this user
        const jobsQuery = query(
          collection(db, 'jobs'),
          where('userId', '==', user.uid)
        );
        
        const jobsSnapshot = await getDocs(jobsQuery);
        const jobIds = jobsSnapshot.docs.map(doc => doc.id);
        
        if (jobIds.length === 0) {
          setLoading(false);
          return;
        }

        // Get all grants for these jobs (revocable only)
        const grantsQuery = query(
          collection(db, 'accessGrants'),
          where('jobId', 'in', jobIds.slice(0, 10)), // Firestore 'in' limit is 10
          where('revocable', '==', true),
          where('isActive', '==', true)
        );
        
        const grantsSnapshot = await getDocs(grantsQuery);
        const grantsList = [];
        
        grantsSnapshot.forEach(doc => {
          const data = doc.data();
          // Don't show owner's own grants
          if (data.userId !== user.uid) {
            grantsList.push({
              id: doc.id,
              ...data
            });
          }
        });
        
        setGrants(grantsList);
      } catch (err) {
        console.error('Error fetching granted access:', err);
        setError('Failed to load granted access');
      } finally {
        setLoading(false);
      }
    };

    fetchGrantedAccess();

    // Refresh every 30 seconds
    const interval = setInterval(fetchGrantedAccess, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleRevoke = async (grant) => {
    if (!window.confirm(`Revoke access for ${grant.requesterName || 'this user'}? They will immediately lose access to this content.`)) {
      return;
    }

    setRevoking(grant.id);
    setError('');

    try {
      await revokeAccessGrant(grant.id, user.uid, 'Access revoked by owner');
      
      // Remove from list
      setGrants(prev => prev.filter(g => g.id !== grant.id));
      
      alert('✅ Access revoked successfully!');
    } catch (err) {
      console.error('Error revoking access:', err);
      setError(err.message || 'Failed to revoke access');
    } finally {
      setRevoking(null);
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'Unknown';
    
    const now = new Date();
    const then = timestamp.toDate();
    const diffMs = now - then;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return 'Recently';
  };

  if (loading) {
    return (
      <div className="granted-access-list">
        <div className="loading-state">⏳ Loading granted access...</div>
      </div>
    );
  }

  if (grants.length === 0) {
    return null; // Don't show panel if no grants
  }

  return (
    <div className="granted-access-list">
      <div className="panel-header">
        <h3>✅ Granted Access ({grants.length})</h3>
        <p className="panel-subtitle">Users you've given free access to your jobs</p>
      </div>

      {error && (
        <div className="error-banner">
          ❌ {error}
        </div>
      )}

      <div className="grants-list">
        {grants.map(grant => (
          <div key={grant.id} className="grant-card">
            <div className="grant-header">
              <div className="user-info">
                <strong className="user-name">
                  {grant.requesterName || 'Anonymous User'}
                </strong>
                <span className="user-email">{grant.requesterEmail}</span>
              </div>
              <span className="grant-time">{getTimeAgo(grant.createdAt)}</span>
            </div>

            <div className="grant-body">
              <div className="grant-meta">
                <span className="meta-item">
                  <strong>Job:</strong> {grant.jobId?.slice(-8)}
                </span>
                <span className="meta-item">
                  <strong>Type:</strong> Free Access
                </span>
              </div>
            </div>

            <div className="grant-actions">
              <button
                onClick={() => handleRevoke(grant)}
                disabled={revoking === grant.id}
                className="revoke-btn"
              >
                {revoking === grant.id ? '⏳ Revoking...' : '🗑️ Revoke Access'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GrantedAccessList;

